/* @jsx h */
import { defineComponent, h, onMounted, onUnmounted } from "vue";

interface Props {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  structuredData?: object;
}

export const SeoHead = defineComponent<Props>({
  props: [
    "title",
    "description",
    "keywords",
    "image",
    "url",
    "type",
    "structuredData",
  ],
  setup(props) {
    const defaultTitle = "Lemmon Eats Guide - Discover the Best Restaurants";
    const defaultDescription =
      "Discover the best restaurants and culinary experiences in Zurich. Find authentic cuisine, expert reviews, and dining recommendations from local food experts.";
    const defaultKeywords =
      "restaurants, food, dining, Zurich, cuisine, reviews, food guide, restaurant guide";
    const defaultImage = "/og-image.jpg";
    const siteName = "Lemmon Eats Guide";

    const title = props.title || defaultTitle;
    const description = props.description || defaultDescription;
    const keywords = props.keywords || defaultKeywords;
    const image = props.image || defaultImage;
    const type = props.type || "website";
    const url =
      props.url || (typeof window !== "undefined" ? window.location.href : "");

    let addedElements: HTMLElement[] = [];

    const updateMetaTags = () => {
      if (typeof document === "undefined") return;

      // Clear previously added elements
      addedElements.forEach((el) => el.remove());
      addedElements = [];

      // Update title
      document.title = title;

      // Helper function to create or update meta tag
      const setMetaTag = (
        selector: string,
        content: string,
        attr = "content",
      ) => {
        let meta = document.querySelector(selector) as HTMLMetaElement;
        if (!meta) {
          meta = document.createElement("meta");
          if (selector.includes("property=")) {
            meta.setAttribute(
              "property",
              selector.match(/property="([^"]+)"/)?.[1] || "",
            );
          } else {
            meta.setAttribute(
              "name",
              selector.match(/name="([^"]+)"/)?.[1] || "",
            );
          }
          document.head.appendChild(meta);
          addedElements.push(meta);
        }
        meta.setAttribute(attr, content);
      };

      // Basic SEO meta tags
      setMetaTag('meta[name="description"]', description);
      setMetaTag('meta[name="keywords"]', keywords);
      setMetaTag(
        'meta[name="robots"]',
        "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      );

      // Open Graph meta tags
      setMetaTag('meta[property="og:title"]', title);
      setMetaTag('meta[property="og:description"]', description);
      setMetaTag('meta[property="og:image"]', image);
      setMetaTag('meta[property="og:url"]', url);
      setMetaTag('meta[property="og:type"]', type);
      setMetaTag('meta[property="og:site_name"]', siteName);

      // Twitter Card meta tags
      setMetaTag('meta[name="twitter:card"]', "summary_large_image");
      setMetaTag('meta[name="twitter:title"]', title);
      setMetaTag('meta[name="twitter:description"]', description);
      setMetaTag('meta[name="twitter:image"]', image);
      setMetaTag('meta[name="twitter:site"]', "@lemmonEats");

      // Canonical link
      let canonical = document.querySelector(
        'link[rel="canonical"]',
      ) as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
        addedElements.push(canonical);
      }
      canonical.href = url;

      // Structured data
      if (props.structuredData) {
        const existingScript = document.querySelector(
          "script[data-seo-structured-data]",
        );
        if (existingScript) {
          existingScript.remove();
        }

        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-structured-data", "true");
        script.textContent = JSON.stringify(props.structuredData);
        document.head.appendChild(script);
        addedElements.push(script);
      }
    };

    onMounted(() => {
      updateMetaTags();
    });

    onUnmounted(() => {
      addedElements.forEach((el) => el.remove());
    });

    return () => null;
  },
});
